import { useAppSelectionStore, useFloatingPanelStore, usePanelStore } from '@host/stores';
import { isPostHogEnabled } from '@host/config/posthog';
import { trackEvent } from '@host/utils/posthog-track';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';

// Office and meeting are the home views users keep landing back on; their usage is measured by
// room and meeting events instead. Chat apps have their own chat_opened / chat_closed.
const UNTRACKED_APPS = new Set(['office', 'meeting', 'chat', 'rocketchat', 'chatbot']);

type AppSurface = 'main' | 'panel' | 'window';

// Keyed by where the app is shown: a main-view slot name, a floating panel id, or the single
// pop-out window that window-target apps (e.g. from Additional Apps) open in.
const openApps = new Map<string, { appId: string; surface: AppSurface; openedAt: number }>();

const closeApp = (key: string, closeReason: 'closed' | 'page_unload') => {
  const current = openApps.get(key);
  if (!current) return;
  openApps.delete(key);
  trackEvent(
    'app_closed',
    {
      app_id: current.appId,
      surface: current.surface,
      duration_seconds: Math.round((Date.now() - current.openedAt) / 1000),
      close_reason: closeReason,
    },
    // A normal request is dropped when the page is going away; a beacon still gets through.
    closeReason === 'page_unload' ? { transport: 'sendBeacon' } : undefined
  );
};

/** Emits app_opened / app_closed when the app shown at `key` changes; repeated calls are no-ops. */
const setAppShown = (
  key: string,
  surface: AppSurface,
  appId: string | null | undefined,
  viewId?: string | null
) => {
  const shown = appId && !UNTRACKED_APPS.has(appId) ? appId : null;
  if (openApps.get(key)?.appId === shown) return;
  closeApp(key, 'closed');
  if (!shown) return;
  openApps.set(key, { appId: shown, surface, openedAt: Date.now() });
  trackEvent('app_opened', {
    app_id: shown,
    view_id: viewId ?? null,
    surface,
    is_guest: isCurrentUserGuest(),
  });
};

/**
 * Watches the stores that decide which app is on screen, so every launcher (sidebar, toolbar,
 * Additional Apps, right-click menu...) is covered. The selection restored on page load is not
 * reported as an open, since subscribers only see changes made after registration.
 */
export const registerAppTracking = () => {
  if (!isPostHogEnabled) return;

  useAppSelectionStore.subscribe((state, prev) => {
    if (state.slotSelections === prev.slotSelections) return;
    for (const [slot, selection] of Object.entries(state.slotSelections)) {
      // Only slots the user actually changed; the rest still hold the selection restored
      // from storage, which openApps never saw.
      const before = prev.slotSelections[slot];
      if (before?.appId === selection?.appId && before?.viewId === selection?.viewId) continue;
      setAppShown(`slot:${slot}`, 'main', selection?.appId, selection?.viewId);
    }
  });

  // Panels are reconciled once per tick: some callers hide and reopen the same panel in one go
  // (e.g. the breakout panel on every data refresh), which is not a close and a new open.
  let panelSyncQueued = false;
  const syncPanels = () => {
    panelSyncQueued = false;
    const visible = usePanelStore.getState().panels.filter((panel) => !panel.isHidden);
    for (const panel of visible) {
      setAppShown(`panel:${panel.id}`, 'panel', panel.appId, panel.viewName);
    }
    for (const key of [...openApps.keys()]) {
      if (key.startsWith('panel:') && !visible.some((panel) => `panel:${panel.id}` === key)) {
        closeApp(key, 'closed');
      }
    }
  };
  usePanelStore.subscribe(() => {
    if (panelSyncQueued) return;
    panelSyncQueued = true;
    queueMicrotask(syncPanels);
  });

  // Also once per tick: the toolbar opens the window before swapping in the new app, and the
  // previous app's details linger after a close, so the in-between state is not a real open.
  let windowSyncQueued = false;
  const syncWindow = () => {
    windowSyncQueued = false;
    const { floatingPanelOpen, floatingPanelDetails } = useFloatingPanelStore.getState();
    const details = floatingPanelOpen ? floatingPanelDetails : null;
    setAppShown('window', 'window', details?.appId, details?.viewName);
  };
  useFloatingPanelStore.subscribe((state, prev) => {
    if (
      windowSyncQueued ||
      (state.floatingPanelOpen === prev.floatingPanelOpen &&
        state.floatingPanelDetails === prev.floatingPanelDetails)
    ) {
      return;
    }
    windowSyncQueued = true;
    queueMicrotask(syncWindow);
  });

  window.addEventListener('pagehide', () => {
    for (const key of [...openApps.keys()]) closeApp(key, 'page_unload');
  });
};
